import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ProductGroupStatistics } from '../ProductGroupStatistics';
import SelectionBulkContainer from '../../../../shop/selectionlists/header/SelectionBulkContainer';
import SelectionBulkMenu from '../../../../shared/components/SelectionBulkMenu';
import {
    BUTTON_SIZE_LARGE,
    BUTTON_THEME_EMPTY_GREEN,
    BUTTON_THEME_FULL_GREEN
} from '../../../../shared/common/Button/themes';
import ConfirmationModalLayout from '../../../../shared/components/modal/ConfirmationModalLayout';
import {
    StBackButton,
    StButtonTextWidthAuto,
    StCheck,
    StContainer,
    StHeadlinePart,
    StMoreButton,
    StNameInput,
    StTitle,
    StTitleForm
} from './Headline.styled';
import { MATERIAL_ICON_EDIT, MATERIAL_ICON_SORT_BY } from '../../../../shared/common/Button/Icon';
import EditPropertiesModal from './EditPropertiesModal';
import { CAT_MAN_PRODUCT_GROUP_SORTING } from '../../../../shared/common/SortByDropDown';
import { Tooltip } from '../../../../shared/components/Tooltip';
import { SelectionList } from '../../../../../models/SelectionList';
import {
    BulkAddProductsToSelectionListResponse,
    BulkAddProductsToShoppingCartResponse,
    Title,
    UsageStatisticsOut
} from '../../../../store/api';
import { useTranslation } from 'react-i18next';
import { ButtonIconTextWidthAuto } from '../../../../shared/common/Button/ButtonIconTextWidthAuto';
import { SubscriptionPeriodType } from '../../../../../models/Article';
import { PriceTier } from '../../../../../models/PriceTier';
import { ProductSelection } from '../../../../shared/product-selection/ProductSelectionTypes';
import { getProductSelectionCount } from '../../../../shared/product-selection/ProductSelectionUtils';
import { orderTitlesByLocale } from './mappers';
import styled from 'styled-components';
import { HelpTooltip } from '../../../../shared/common/HelpTooltip';
import { useParams } from 'react-router';
import { useIsAdmin } from '../../../../shared/utils';
import { ImportIsbnDialog } from '../../../../shop/selectionlists/header/ImportIsbnDialog';
import { useAddProductsToGroupByIsbns } from '../../../../api/library-managed/useAddProductsToGroupByIsbns';

const StTooltip = styled(HelpTooltip)`
    margin: 0 10px 0 -5px;
    & i {
        color: #000000;
        font-size: 13px;
    }
`;

interface Props {
    active: boolean;
    title: string;
    isInSelectionMode: boolean;
    isAddToListEnabled: boolean;
    onRemoveFromProductGroup: () => void;
    productSelection: ProductSelection;
    cancelSelectionMode: () => void;
    usageStatistics: UsageStatisticsOut | undefined;
    onSaveTitle: (title: string) => void;
    onToggleActive: (active: boolean) => void;
    onRemoveAllProducts: () => void;
    onBulkAddToGroup: (productGroupId: number) => void;
    onBulkAddToCart: (
        performChecks: boolean,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType
    ) => Promise<BulkAddProductsToShoppingCartResponse>;
    productAmount: number;
    sorting: string;
    onAddToList: (
        list: SelectionList,
        performChecks: boolean,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    isNothingSelected: boolean;
    productGroupTitles: Title[] | undefined;
    hasSingleUsageCondition: boolean;
}

const Headline: FC<Props> = ({
    title,
    active,
    isAddToListEnabled,
    onSaveTitle,
    usageStatistics = {
        ebooksAmountOnSite: 0,
        ebooksOnLoanOnSite: 0,
        ebooksLoanableOnSite: 0,
        eaudiobooksAmountOnSite: 0,
        eaudiobooksOnLoanOnSite: 0,
        eaudiobooksLoanableOnSite: 0,
        epressAmountOnSite: 0,
        epressOnLoanOnSite: 0,
        epressLoanableOnSite: 0
    },
    productGroupTitles,
    onBulkAddToGroup,
    cancelSelectionMode,
    productAmount,
    isInSelectionMode,
    onRemoveAllProducts,
    productSelection,
    onRemoveFromProductGroup,
    onToggleActive,
    onBulkAddToCart,
    sorting,
    onAddToList,
    isNothingSelected,
    hasSingleUsageCondition
}) => {
    const {
        t,
        i18n: { language }
    } = useTranslation();
    const titleFormRef = useRef() as React.MutableRefObject<HTMLInputElement>;
    const spacerRef = useRef() as React.MutableRefObject<HTMLInputElement>;
    const titleRef = useRef() as React.MutableRefObject<HTMLInputElement>;
    const editOrderRef = useRef(null);
    const navigate = useNavigate();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [editTitle, setEditTitle] = useState(false);
    const [productTitle, setProductTitle] = useState<string>(title);
    const [visible, setVisible] = useState(false);
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const [showIsbnDialog, setShowIsbnDialog] = useState(false);
    const sortingKey = useMemo(() => {
        return CAT_MAN_PRODUCT_GROUP_SORTING.find(item => item.value === sorting) ?? CAT_MAN_PRODUCT_GROUP_SORTING[0];
    }, [sorting]);

    const { productGroupId = '' } = useParams<'productGroupId'>();
    const isAdminRole = useIsAdmin();

    const { mutateAsync: addProductsByIsbns, isLoading: isLoadingImportIsbns } = useAddProductsToGroupByIsbns(
        productGroupId,
        () => {
            setShowIsbnDialog(false);
        }
    );

    const orderedTitles = useMemo(
        () => orderTitlesByLocale(productGroupTitles, language),
        [language, productGroupTitles]
    );

    function startTitleEdit() {
        if (!editTitle) {
            setEditTitle(true);
            setProductTitle(title);
        }
        titleRef.current.focus();
    }

    function updateTitle(value: string) {
        setProductTitle(value);
    }

    function triggerSaveTitle() {
        if (!productTitleValid) {
            return;
        }

        if (productTitle !== title) {
            onSaveTitle(productTitle);
        }
        setEditTitle(false);
        titleRef.current.blur();
    }

    function handleShowConfirmation(state: boolean) {
        setShowConfirmation(state);
    }

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (titleFormRef.current && !titleFormRef.current.contains(event.target as Node)) {
                setEditTitle(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const productTitleValid = productTitle.trim().length >= 1 && productTitle.length <= 255;

    const moreButton = (
        <StMoreButton className="default-dd-nav expanded-dd list-actions fl-none centered" title="more actions...">
            <a data-toggle-dropdown className="bt-dd" data-displayname="MoreActionsMenu" aria-label="More Actions">
                <i className="material-icons">more_horiz</i>
            </a>
            <div data-close-dropdown className="default-dd headline-dropdown-overwrite">
                <span style={{ right: '80px' }} className="active-arrow" />
                <ul>
                    <li>
                        <a
                            onClick={() => handleShowConfirmation(true)}
                            className="display-block fit-content"
                            data-displayname="RemoveAllProductsOption"
                        >
                            <i className="material-icons normal fl-left">delete</i>
                            {t('marketing.productGroups.removeAllProducts')}
                        </a>
                    </li>
                    {isAdminRole ? (
                        <li>
                            <div className="default-dd-nav bt-default fl-left">
                                <a onClick={() => setShowIsbnDialog(true)} className="display-block isbn-import">
                                    <i className="bolinda-icons fl-left">bolinda_only</i> {t('isbn.importIsbns')}
                                </a>
                            </div>
                        </li>
                    ) : null}
                </ul>
            </div>
        </StMoreButton>
    );

    const bulkMenu = (
        <SelectionBulkMenu
            isInSelectionMode={isInSelectionMode}
            isAddToListEnabled={isAddToListEnabled}
            cancelSelection={cancelSelectionMode}
            selectedCount={getProductSelectionCount(productSelection)}
            onRemoveFrom={onRemoveFromProductGroup}
            onBulkAddToGroup={onBulkAddToGroup}
            onBulkAddToCart={onBulkAddToCart}
            deleteLabel={t('marketing.productGroups.removeFromProductGroup')}
            isDeleteFromEnabled={true}
            isAddToProductGroupEnabled
            onBulkAddToList={onAddToList}
            isNothingSelected={isNothingSelected}
            hasSingleUsageCondition={hasSingleUsageCondition}
        />
    );

    const header = (
        <StContainer>
            <ConfirmationModalLayout
                visible={showConfirmation}
                title={t('marketing.productGroups.removeAllProducts')}
                text={t('marketing.productGroups.confirmRemove')}
                onConfirm={() => {
                    onRemoveAllProducts();
                    handleShowConfirmation(false);
                }}
                onCancel={() => handleShowConfirmation(false)}
            />
            {visible ? (
                <EditPropertiesModal
                    productGroupId={productGroupId}
                    orderedTitles={orderedTitles}
                    title={t('marketing.productGroups.editProperties')}
                    hideModal={() => setVisible(false)}
                    sorting={sortingKey}
                />
            ) : null}
            <StHeadlinePart>
                <StBackButton onClick={() => window.history.back()}>
                    <i className="material-icons" style={{ fontSize: 'inherit' }}>
                        arrow_back
                    </i>
                </StBackButton>
                <StTitle>
                    <StTitleForm ref={titleFormRef} className="list-name-form" active={editTitle}>
                        <StNameInput
                            ref={titleRef}
                            disableInput={(productGroupTitles?.length ?? 0) > 1}
                            value={editTitle ? productTitle : title}
                            minWidthInPx={50}
                            onClick={startTitleEdit}
                            onChange={updateTitle}
                            onEnterKey={triggerSaveTitle}
                            onEscKey={() => {
                                setEditTitle(false);
                            }}
                            truncated={!editTitle}
                            onFocus={startTitleEdit}
                            maxLength={80}
                        />
                        <StCheck
                            onClick={triggerSaveTitle}
                            className="material-icons"
                            active={editTitle && productTitleValid}
                        >
                            check
                        </StCheck>
                    </StTitleForm>
                </StTitle>
                {(productGroupTitles?.length || 0) > 1 ? (
                    <StTooltip size="medium" text={t('marketing.productGroups.productGroupTitles.infoTooltipText')} />
                ) : null}
                {moreButton}
                <div ref={spacerRef} style={{ flexGrow: 1 }} />
            </StHeadlinePart>
            <StHeadlinePart>
                <ProductGroupStatistics usageStatistics={usageStatistics} />

                <ButtonIconTextWidthAuto
                    size={BUTTON_SIZE_LARGE}
                    onClick={() => setVisible(true)}
                    theme={BUTTON_THEME_EMPTY_GREEN}
                    icon={MATERIAL_ICON_EDIT}
                    className="ml-2"
                    dataDisplayname="EditPropertiesButton"
                >
                    {t('marketing.productGroups.editProperties')}
                </ButtonIconTextWidthAuto>
                <div
                    ref={editOrderRef}
                    onMouseOver={() => setIsTooltipVisible(true)}
                    onMouseOut={() => setIsTooltipVisible(false)}
                >
                    <ButtonIconTextWidthAuto
                        size={BUTTON_SIZE_LARGE}
                        onClick={() => {
                            navigate('sorting');
                        }}
                        disabled={sorting !== 'custom'}
                        theme={BUTTON_THEME_EMPTY_GREEN}
                        icon={MATERIAL_ICON_SORT_BY}
                        dataDisplayname="EditProductOrderButton"
                        className="ml-2"
                    >
                        {t('marketing.productGroups.editCustomProductOrder')}
                    </ButtonIconTextWidthAuto>
                </div>
                <Tooltip
                    size="large"
                    target={editOrderRef}
                    visible={sortingKey?.value !== 'custom' && isTooltipVisible}
                    placement="top"
                >
                    <p>{t('marketing.productGroups.editTooltip')}</p>
                </Tooltip>

                <StButtonTextWidthAuto
                    size={BUTTON_SIZE_LARGE}
                    theme={active ? BUTTON_THEME_EMPTY_GREEN : BUTTON_THEME_FULL_GREEN}
                    onClick={() => onToggleActive(!active)}
                    disabled={!active && productAmount === 0}
                    dataDisplayname={'ToggleActiveButton'}
                    className="ml-2"
                >
                    {active
                        ? t('marketing.productGroups.deactivateProductGroup')
                        : t('marketing.productGroups.activateProductGroup')}
                </StButtonTextWidthAuto>
            </StHeadlinePart>
        </StContainer>
    );
    return (
        <>
            <SelectionBulkContainer
                movedUp={isInSelectionMode}
                isSticky={false}
                headerComponent={header}
                bulkMenuComponent={bulkMenu}
            />
            <ImportIsbnDialog
                onCancel={() => setShowIsbnDialog(false)}
                onImport={addProductsByIsbns}
                show={showIsbnDialog}
                isLoading={isLoadingImportIsbns}
            />
        </>
    );
};

export default Headline;
